import { useTranslation } from 'react-i18next';
import { applyPatch } from 'fast-json-patch';
import { useDocumentStore } from '../../stores';
import { useNavigate } from 'react-router-dom';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useMemo, useRef, useState } from 'react';
import { appInsights } from '../../../core/analytics/applicationInsights';
import { discardChangesEvent, finishAssistanceEvent, sendToLabelingEvent } from '../../../core/analytics/customEvents';
import { camelCase, merge } from 'lodash';
import { url } from '../../../core/utils/link';
import { useErrorAlert } from './components/AssistanceHeader.tsx';
import { unpackAndMergeDynamicSchemaFields } from '../../../core/utils';
import {
    getUserFilterStorageKeyFromRecord,
    ORDER_BY_FILTER_KEY,
    useOverviewFilters,
    useUserFilters,
} from '../../../core/utils/filterQuery.ts';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useBoolean } from '../../../core/utils/hooks/useBoolean.ts';
import useSerializedQueue from '../../../core/utils/hooks/useSerializedQueue.ts';
import { getRecordListPath } from '../../utils.ts';

const STOCK_REQUEST_ACTIONS = ['action:request_stock', 'action:request_stock_all'];

// Never executed; placeholder document so useMutation can be called unconditionally when a
// documentConfiguration has no REOPEN_FOR_ASSISTANCE mutation (e.g. DBImmo)
const NOOP_MUTATION = gql`
    mutation NoopReopenForAssistance {
        __typename
    }
`;

const adjustPatch = (patch) => {
    const applyConvertToTypename = (data) => {
        if (typeof data === 'object' && data !== null) {
            if ('__name__' in data) {
                data['__typename'] = data['__name__'];
                delete data['__name__'];
            }
            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    applyConvertToTypename(data[key]);
                }
            }
        } else if (Array.isArray(data) && data !== null) {
            data.forEach((elem) => {
                applyConvertToTypename(elem);
            });
        }
    };
    applyConvertToTypename(patch);
    return patch;
};

const useAssistanceHandlers = ({
    user,
    documentConfiguration,
    recordId,
    record,
    setRecord,
    dataRefetch,
    lock,
    cleanLock,
    pagination,
    onDocumentPatched = undefined,
    assistanceSession,
}) => {
    const { t } = useTranslation('assistance');
    const { publishToast } = useToaster();

    const documentStore = useDocumentStore();
    const navigate = useNavigate();

    const [update, { loading: updateLoading, error: updateError }] = documentStore.useUpdateDocument(
        documentConfiguration.documentType
    );
    const [reselect, { loading: reselectLoading, error: reselectError }] = documentStore.useReselectDocument(
        documentConfiguration.documentType
    );
    const [retryStep, { loading: retryStepLoading, error: retryStepError }] = useMutation(
        documentConfiguration.RETRY_STEP
    );

    // Serialization queue: each mutation waits for the previous to settle before firing
    const enqueue = useSerializedQueue();

    // Ref to always hold the latest record, avoiding stale closures without functional updaters
    const recordRef = useRef(record);
    recordRef.current = record;

    // DBImmo documentConfigurations have no reopen for assistance mutation; the hook still
    // needs to be called unconditionally (rules of hooks), so fall back to a noop document
    const hasReopenForAssistance = Boolean(documentConfiguration.REOPEN_FOR_ASSISTANCE);
    const [reopenForAssistanceMutation, { loading: reopenForAssistanceLoading }] = useMutation(
        documentConfiguration.REOPEN_FOR_ASSISTANCE ?? NOOP_MUTATION
    );
    const reopenForAssistance = hasReopenForAssistance ? reopenForAssistanceMutation : null;

    const [sendToLabeling, { loading: sendToLabelingLoading, error: sendToLabelingError }] = useMutation(
        documentConfiguration.SEND_TO_LABELING
    );

    const [discardModalVisible, setDiscardModalVisible] = useState(false);
    const [isMoveDocumentVisible, setIsMoveDocumentVisible] = useState(false);
    const [isDeleteModalVisible, showDeleteModal, hideDeleteModal] = useBoolean(false);

    const MASTERDATA_VIEW_MODE_STORAGE_KEY = 'masterdataViewMode';

    const [masterdataBrowserModalVisible, setMasterdataBrowserModalVisible] = useState(false);
    const [masterdataBrowserSidePanelVisible, setMasterdataBrowserSidePanelVisible] = useState(false);
    const [masterdataBrowserModalState, setMasterdataBrowserModalState] = useState({
        searchTerm: null,
        targetField: null,
    });

    const openMasterdataBrowser = useCallback((triggerState: any) => {
        setMasterdataBrowserModalState(triggerState);

        // get saved view mode preference for this session from localStorage, if no value, default to modal view
        const savedViewMode = localStorage.getItem(MASTERDATA_VIEW_MODE_STORAGE_KEY) || 'modal';

        if (savedViewMode === 'sidepanel') {
            setMasterdataBrowserSidePanelVisible(true);
        } else {
            setMasterdataBrowserModalVisible(true);
        }
    }, []);

    const closeMasterdataBrowser = useCallback(() => {
        setMasterdataBrowserModalVisible(false);
        setMasterdataBrowserSidePanelVisible(false);
        setMasterdataBrowserModalState({
            searchTerm: null,
            targetField: null,
        });
    }, []);

    const switchToSidePanel = useCallback(() => {
        localStorage.setItem(MASTERDATA_VIEW_MODE_STORAGE_KEY, 'sidepanel');
        setMasterdataBrowserModalVisible(false);
        setMasterdataBrowserSidePanelVisible(true);
    }, []);

    const switchToModal = useCallback(() => {
        localStorage.setItem(MASTERDATA_VIEW_MODE_STORAGE_KEY, 'modal');
        setMasterdataBrowserSidePanelVisible(false);
        setMasterdataBrowserModalVisible(true);
    }, []);

    const stepRun = record?.stepRun || {};

    const channelId = record?.channel?.id;
    const testing = record?.isTestDocument || false;
    const finished = record?.stepRun?.executionStatus === 'SUCCEEDED' || record?.deletedAt;
    const [userFilters] = useUserFilters({
        storageKey: getUserFilterStorageKeyFromRecord(documentConfiguration.documentTypeName, record),
        shouldReadLocalStorage: false,
        defaultPageSize: undefined,
    });
    const overviewFilters = useOverviewFilters({ userFilters, channelId, testing, finished });

    const orderBy = overviewFilters.find((f) => f.name === ORDER_BY_FILTER_KEY)?.value;
    const [getNextAssistanceRecord] = useLazyQuery(documentConfiguration.GET_NEXT_ASSISTANCE_RECORD, {
        variables: {
            channelId,
            filters: overviewFilters,
            orderBy: Array.isArray(orderBy) ? orderBy[0] : orderBy,
        },
        fetchPolicy: 'network-only',
    });

    const handleUpdate = useCallback(
        ({
            fieldName = undefined,
            itemIndex = undefined,
            itemType = undefined,
            action = 'update',
            payload = undefined,
            forceRefetchRecord = false,
        }) => {
            /*
            fieldName: name of the field
            itemIndex (optional): leave undefined for header data, index of item for line items
            action (optional): by default update, can also be used to e.g. delete a line item, copy data, ...
            payload (optional): usually containing updated values, can be different for other actions
            itemType (optional): current itemType if multiple item views are available
            forceRefetchRecord (optional): if true, the record will be refetched after the update
            */
            payload = payload || {};

            return enqueue(() =>
                update({
                    variables: {
                        recordId,
                        action,
                        fieldName,
                        itemIndex,
                        itemType,
                        payload,
                    },
                    fetchPolicy: 'no-cache',
                })
                    .then((res: any) => {
                        const currentRecord = recordRef.current;
                        const patch =
                            res?.data?.updateDocument?.patch ||
                            res?.data?.[documentConfiguration?.documentTypeName + 'Update']?.patch;
                        const updatedRecord =
                            res?.data?.updateDocument?.record ||
                            res?.data?.[documentConfiguration?.documentTypeName + 'Update']?.record ||
                            {};

                        const stockRequestPerformed =
                            res?.data?.[documentConfiguration?.documentTypeName + 'Update']?.stockRequestPerformed;
                        if (STOCK_REQUEST_ACTIONS.includes(action) && stockRequestPerformed === false) {
                            publishToast({
                                description: t('itemsView.actions.request_stock_no_result'),
                                status: 'warning',
                            });
                        }

                        if (patch) {
                            const updatedDocument = applyPatch(
                                unpackAndMergeDynamicSchemaFields(
                                    currentRecord[`${documentConfiguration?.documentTypeName}Unsaved`]
                                ),
                                adjustPatch(patch)
                            ).newDocument;
                            const unpackedDocument = unpackAndMergeDynamicSchemaFields(updatedDocument);
                            // we need to do the spread here as we have some areas that rely on if it is a new object or not
                            const newRecord = {
                                // updatedRecord can still contain information that is not in the patch like if the record is locked or can be completed
                                ...merge(currentRecord, updatedRecord),
                                [`${documentConfiguration?.documentTypeName}Unsaved`]: {
                                    ...unpackedDocument,
                                },
                            };
                            // Update ref synchronously so the next enqueued mutation
                            // applies its patch against the correct base state
                            recordRef.current = newRecord;
                            setRecord(newRecord);

                            // Notify callers about patching the document so they can sync derived state
                            // (currently only used for list of services) without a stale closure on record
                            onDocumentPatched?.(unpackedDocument);
                        } else if (updatedRecord) {
                            // fallback if no patch is returned e.g. legacy cases
                            recordRef.current = updatedRecord;
                            setRecord(updatedRecord);
                        }

                        // This is a quick hack until all use cases work properly with patches,
                        // or we come up with a different solution
                        if (forceRefetchRecord) {
                            return dataRefetch();
                        }
                    })
                    .catch((error) => {
                        console.error('Error updating record', error);
                        // on error we try to fallback to refetching the whole record
                        // this can happen when there is more data in the backend then we queried originally
                        // from the frontend which would result in json patch to fail
                        // Also we're rejecting the promise, so all subscribers can know that originally update is failed
                        return Promise.reject(dataRefetch());
                    })
            );
        },
        [recordId, dataRefetch]
    );

    const handleReselect = useCallback(
        ({
            fieldName = undefined,
            bbox = undefined,
            pageIndex = undefined,
            itemIndex = undefined,
            itemType = undefined,
            isEmailOcr = false,
        }) => {
            // itemIndex -1 means add new line item

            // Do not trigger a reselect event if the bbox tuples are the same.
            // This happens when the user clicks on the document, so the bbox is just a point,
            // and we don't want to trigger an event for that.
            if (bbox && JSON.stringify(bbox[0]) === JSON.stringify(bbox[1])) {
                return;
            }

            return enqueue(() =>
                // This way we can mock an actual backend call
                reselect({
                    variables: {
                        recordId,
                        fieldName,
                        bbox,
                        pageIndex,
                        itemIndex,
                        itemType,
                        isEmailOcr,
                    },
                    fetchPolicy: 'no-cache',
                })
                    .then((res: any) => {
                        const currentRecord = recordRef.current;
                        const patch = res?.data?.[documentConfiguration?.documentTypeName + 'Reselect']?.patch;
                        const updatedRecord =
                            res?.data?.[documentConfiguration?.documentTypeName + 'Reselect']?.record || {};

                        if (patch) {
                            const updatedDocument = applyPatch(
                                unpackAndMergeDynamicSchemaFields(
                                    currentRecord[`${documentConfiguration?.documentTypeName}Unsaved`]
                                ),
                                adjustPatch(patch)
                            ).newDocument;
                            // we need to do the spread here as we have some areas that rely on if it is a new object or not
                            const newRecord = {
                                // updatedRecord can still contain information that is not in the patch like if the record is locked or can be completed
                                ...merge(currentRecord, updatedRecord),
                                [`${documentConfiguration?.documentTypeName}Unsaved`]: {
                                    ...unpackAndMergeDynamicSchemaFields(updatedDocument),
                                },
                            };
                            recordRef.current = newRecord;
                            setRecord(newRecord);
                        } else if (updatedRecord) {
                            // fallback if no patch is returned e.g. legacy cases
                            recordRef.current = updatedRecord;
                            setRecord(updatedRecord);
                        }
                    })
                    .catch((error) => {
                        console.error('Error updating record', error);
                        // on error we try to fallback to refetching the whole record
                        // this can happen when there is more data in the backend then we queried originally
                        // from the frontend which would result in json patch to fail
                        return dataRefetch();
                    })
            );
        },
        [recordId, dataRefetch]
    );

    const handleReset = useCallback(() => {
        appInsights?.trackEvent(
            ...discardChangesEvent(user, record, record?.[documentConfiguration?.documentTypeName + 'Unsaved'])
        );
        return handleUpdate({ action: `action:reset`, payload: { stepRunId: stepRun.id }, forceRefetchRecord: true });
    }, [handleUpdate, stepRun.id, history, record]);

    const redirectToNextRecord = useCallback(() => {
        return getNextAssistanceRecord().then(({ data }) => {
            const nextRecordFieldName = camelCase(
                'next_' + documentConfiguration?.documentTypeName + 'ProcessingRecord'
            );
            const isTesting = record?.isTestDocument;
            const isFinished = stepRun?.executionStatus === 'SUCCEEDED';
            const nextAssistanceRecordId = data?.[nextRecordFieldName]?.id;
            const nextPaginationRecordId = pagination?.nextRecordId || pagination?.previousRecordId;
            // the next assistance lookup never matches status filters like FAILED, so walk the filtered list instead
            const hasStatusFilter = userFilters.some((f) => f.name === 'status');

            if (!isFinished && !isTesting && nextAssistanceRecordId != null) {
                // in case the user opened a assistable record and finished or deleted it -> go to next assistable record if there is one
                return navigate(
                    url(
                        documentConfiguration.ASSISTANCE_PATH,
                        { recordId: nextAssistanceRecordId },
                        { keepSearch: true }
                    )
                );
            } else if ((isFinished || isTesting || hasStatusFilter) && nextPaginationRecordId != null) {
                // otherwise check if there is any other record in the pagination and go to that one
                return navigate(
                    url(
                        documentConfiguration.ASSISTANCE_PATH,
                        { recordId: nextPaginationRecordId },
                        { keepSearch: true }
                    )
                );
            } else {
                // no record left: go back to the list the record was opened from, e.g. the channel's list
                const listPath = getRecordListPath(documentConfiguration, {
                    channelId: new URLSearchParams(window.location.search).get('channel'),
                    finished,
                    testing,
                });
                return navigate(url(listPath, undefined, { keepSearch: true }));
            }
        });
    }, [handleUpdate, stepRun.id, history, record, pagination, overviewFilters, userFilters]);

    const handleDiscardDocumentOpen = useCallback(() => setDiscardModalVisible(true), []);
    const handleDiscardDocumentClose = useCallback(() => setDiscardModalVisible(false), []);

    const handleMoveDocumentsOpen = useCallback(() => setIsMoveDocumentVisible(true), []);
    const handleMoveDocumentsClose = useCallback(() => setIsMoveDocumentVisible(false), []);

    const handleReopenForAssistance = useCallback(
        (reason) => {
            return reopenForAssistance({
                variables: {
                    recordId,
                    stepRunId: stepRun.id,
                    reason: reason,
                },
                fetchPolicy: 'no-cache',
            })
                .then(() => {
                    return dataRefetch();
                })
                .then(() => {
                    assistanceSession.restart();
                    return lock();
                });
        },
        [handleUpdate, stepRun.id, history, record]
    );

    const handleFinish = useCallback(
        (event) => {
            appInsights?.trackEvent(
                ...finishAssistanceEvent(user, record, record?.[documentConfiguration?.documentTypeName + 'Unsaved'], {
                    continue: false,
                    ...assistanceSession.finish(record),
                })
            );

            return handleUpdate({
                action: `action:finish`,
                payload: {
                    stepRunId: stepRun.id,
                },
            })
                .then(() => dataRefetch())
                .then(() =>
                    navigate(
                        url(
                            documentConfiguration.ASSISTANCE_TAB_PATH,
                            { recordId, tab: 'result' },
                            { keepSearch: true }
                        )
                    )
                );
        },
        [handleUpdate, stepRun.id, history, record]
    );

    const handleChangeStatus = useCallback(
        (status, reason = undefined) => {
            return handleUpdate({
                action: `action:change_status`,
                payload: {
                    stepRunId: stepRun.id,
                    status,
                    reason,
                },
            }).then(() => dataRefetch());
        },
        [handleUpdate, stepRun.id, history, record]
    );

    const handleFinishAndContinue = useCallback(
        (event) => {
            appInsights?.trackEvent(
                ...finishAssistanceEvent(user, record, record?.[documentConfiguration?.documentTypeName + 'Unsaved'], {
                    continue: true,
                    ...assistanceSession.finish(record),
                })
            );

            return handleUpdate({
                action: `action:finish`,
                payload: { stepRunId: stepRun.id },
            }).then(() => redirectToNextRecord());
        },
        [handleUpdate, stepRun.id, history, record, pagination, overviewFilters]
    );

    const handleRetryStep = useCallback(
        (event) => {
            return retryStep({
                variables: {
                    recordId,
                    stepRunId: stepRun.id,
                },
                fetchPolicy: 'no-cache',
            })
                .then(() => {
                    // go to assistance if step succeeded on retry
                    navigate(
                        url(
                            documentConfiguration.ASSISTANCE_TAB_PATH,
                            { recordId, tab: 'header' },
                            { keepSearch: true }
                        )
                    );
                    // refetch data to show new error message or updated step run
                    void dataRefetch();
                })
                .catch(() => {
                    // refetch data to show new error message
                    void dataRefetch();
                });
        },
        [retryStep, recordId, stepRun.id, history]
    );

    const handleSendToLabeling = useCallback(() => {
        appInsights?.trackEvent(
            ...sendToLabelingEvent(user, record, record?.[documentConfiguration.documentTypeName + 'Unsaved'])
        );
        return sendToLabeling({
            variables: {
                recordId,
            },
            fetchPolicy: 'no-cache',
        }).then(() => {
            return dataRefetch();
        });
    }, [sendToLabeling, record, history, pagination]);

    const updateErrorAlert = useErrorAlert(t('header.alerts.updateError.title'), updateError?.message);
    const reselectErrorAlert = useErrorAlert(t('header.alerts.reselectError.title'), reselectError?.message);
    const retryStepErrorAlert = useErrorAlert(t('header.alerts.retryStepError.title'), retryStepError?.message);

    return useMemo(
        () => ({
            onUpdate: handleUpdate,
            onReselect: handleReselect,
            onReset: handleReset,
            isDiscardDocumentVisible: discardModalVisible,
            onDiscardOpen: handleDiscardDocumentOpen,
            onDiscardClose: handleDiscardDocumentClose,
            isMoveDocumentVisible,
            onMoveDocumentsOpen: handleMoveDocumentsOpen,
            onMoveDocumentsClose: handleMoveDocumentsClose,
            isDeleteDocumentVisible: isDeleteModalVisible,
            onDeleteDocumentOpen: showDeleteModal,
            onDeleteDocumentsClose: hideDeleteModal,
            redirectToNextRecord,
            onFinish: handleFinish,
            onFinishAndContinue: handleFinishAndContinue,
            onRetryStep: handleRetryStep,
            onChangeStatus: handleChangeStatus,
            onReopenForAssistance: handleReopenForAssistance,
            onSendToLabeling: handleSendToLabeling,
            masterdataBrowserModalVisible,
            masterdataBrowserSidePanelVisible,
            masterdataBrowserModalState,
            openMasterdataBrowser,
            closeMasterdataBrowser,
            switchToSidePanel,
            switchToModal,
            alert: updateErrorAlert || reselectErrorAlert || retryStepErrorAlert,
            loading:
                updateLoading ||
                reselectLoading ||
                retryStepLoading ||
                reopenForAssistanceLoading ||
                sendToLabelingLoading,
        }),
        [
            handleUpdate,
            handleReselect,
            handleReset,
            handleFinish,
            handleFinishAndContinue,
            handleRetryStep,
            handleChangeStatus,
            handleReopenForAssistance,
            handleSendToLabeling,
            discardModalVisible,
            updateErrorAlert,
            reselectErrorAlert,
            retryStepErrorAlert,
            updateLoading,
            reselectLoading,
            retryStepLoading,
            masterdataBrowserModalVisible,
            masterdataBrowserSidePanelVisible,
            masterdataBrowserModalState,
            switchToSidePanel,
            switchToModal,
            pagination,
            isMoveDocumentVisible,
            isDeleteModalVisible,
        ]
    );
};

export default useAssistanceHandlers;
